import React, { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import moment from 'moment';
import Select from 'react-select';
import './ApplyLeave.css';

export default function ApplyLeave() {
    const { http, user } = AuthUser();
    const [leaves, setLeaves] = useState([]);
    const [currentMonth, setCurrentMonth] = useState(moment());
    const [showModal, setShowModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    
    const [formData, setFormData] = useState({
        leave_applied: '',
        reason: '',
        report_to: '',
        staff_id: user.id,
        company_id: user.company_id
    });

    useEffect(() => {
        fetchLeaves();
        fetchUsers();
    }, []);

    const fetchLeaves = async () => {
        try {
            const response = await http.get(`/leave_applied?staff_id=${user.id}&company_id=${user.company_id}`);
            if (response.data.success) {
                setLeaves(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching leaves:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await http.get(`/users_by_company_department?company_id=${user.company_id}&department_id=1`);
            // Check different possible response formats
            if (response.data.success && Array.isArray(response.data.users)) {
                setUsers(response.data.users);
            } else if (Array.isArray(response.data.users)) {
                setUsers(response.data.users);
            } else if (Array.isArray(response.data.data)) {
                setUsers(response.data.data);
            } else if (Array.isArray(response.data)) {
                setUsers(response.data);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            // Fallback to get all users by company
            try {
                const fallbackResponse = await http.get(`/department_user_company?login_user=${user.id}`);
                if (Array.isArray(fallbackResponse.data.users)) {
                    setUsers(fallbackResponse.data.users);
                }
            } catch (fallbackError) {
                console.error('Error fetching users fallback:', fallbackError);
            }
        }
    };

    const handleDateClick = (date) => {
        const dateStr = moment(date).format('YYYY-MM-DD');
        const existingLeave = leaves.find(l => 
            moment(l.leave_applied).format('YYYY-MM-DD') === dateStr
        );
        
        setSelectedDate(date);
        
        if (existingLeave) {
            // Edit mode - show existing leave
            setSelectedLeave(existingLeave);
            setIsEditMode(true);
            setFormData({
                leave_applied: moment(existingLeave.leave_applied).format('YYYY-MM-DD'),
                reason: existingLeave.reason || '',
                report_to: existingLeave.report_to || '',
                staff_id: existingLeave.staff_id || user.id,
                company_id: existingLeave.company_id || user.company_id
            });
        } else {
            // Add mode - new leave
            setSelectedLeave(null);
            setIsEditMode(false);
            setFormData({
                leave_applied: dateStr,
                reason: '',
                report_to: '',
                staff_id: user.id,
                company_id: user.company_id
            });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            const requestData = {
                ...formData,
                login_user: user.id
            };
            
            let response;
            if (isEditMode && selectedLeave) {
                // Update existing leave
                response = await http.put(`/leave_applied_update/${selectedLeave.id}`, requestData);
            } else {
                // Create new leave
                response = await http.post('/leave_applied_add', requestData);
            }
            
            if (response.data.success) {
                toast.success(isEditMode ? 'Leave updated successfully!' : 'Leave applied successfully!');
                setShowModal(false);
                setFormData({
                    leave_applied: '',
                    reason: '',
                    report_to: '',
                    staff_id: user.id,
                    company_id: user.company_id
                });
                setSelectedLeave(null);
                setIsEditMode(false);
                fetchLeaves();
            } else {
                toast.error(response.data.message || 'Failed to save leave');
            }
        } catch (error) {
            console.error('Error saving leave:', error);
            toast.error(error.response?.data?.message || 'Error saving leave');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedLeave || !window.confirm('Are you sure you want to delete this leave application?')) {
            return;
        }
        
        setLoading(true);
        try {
            const response = await http.delete(`/leave_applied_delete/${selectedLeave.id}`);
            if (response.data.success) {
                toast.success('Leave deleted successfully!');
                setShowModal(false);
                setSelectedLeave(null);
                setIsEditMode(false);
                fetchLeaves();
            } else {
                toast.error(response.data.message || 'Failed to delete leave');
            }
        } catch (error) {
            console.error('Error deleting leave:', error);
            toast.error(error.response?.data?.message || 'Error deleting leave');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleReportToChange = (selectedOption) => {
        setFormData({
            ...formData,
            report_to: selectedOption ? selectedOption.value : ''
        });
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedDate(null);
        setSelectedLeave(null);
        setIsEditMode(false);
        setFormData({
            leave_applied: '',
            reason: '',
            report_to: '',
            staff_id: user.id,
            company_id: user.company_id
        });
    };

    const getLeaveStatus = (date) => {
        const leave = leaves.find(l => 
            moment(l.leave_applied).format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD')
        );
        if (!leave) return null;
        if (leave.is_approved === 1) return 'approved';
        if (leave.is_approved === 2) return 'rejected';
        if (leave.is_approved === 0) return 'pending';
        return 'pending'; // default to pending if null or undefined
    };

    const renderCalendar = () => {
        const startOfMonth = currentMonth.clone().startOf('month');
        const endOfMonth = currentMonth.clone().endOf('month');
        const startDate = startOfMonth.clone().startOf('week');
        const endDate = endOfMonth.clone().endOf('week');
        
        const days = [];
        const day = startDate.clone();
        
        while (day.isSameOrBefore(endDate, 'day')) {
            days.push(day.clone());
            day.add(1, 'day');
        }

        const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        
        return (
            <div className="beautiful-calendar">
                <div className="calendar-header">
                    <button 
                        className="calendar-nav-btn"
                        onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, 'month'))}
                    >
                        <i className="fa fa-chevron-left"></i>
                    </button>
                    <h3 className="calendar-month-year">
                        {currentMonth.format('MMMM YYYY')}
                    </h3>
                    <button 
                        className="calendar-nav-btn"
                        onClick={() => setCurrentMonth(currentMonth.clone().add(1, 'month'))}
                    >
                        <i className="fa fa-chevron-right"></i>
                    </button>
                </div>
                
                <div className="calendar-weekdays">
                    {weekDays.map(day => (
                        <div key={day} className="calendar-weekday">{day}</div>
                    ))}
                </div>
                
                <div className="calendar-days">
                    {days.map((date, index) => {
                        const isCurrentMonth = date.isSame(currentMonth, 'month');
                        const isToday = date.isSame(moment(), 'day');
                        const leaveStatus = getLeaveStatus(date);
                        const hasLeave = leaveStatus !== null;
                        
                        let dayClass = 'calendar-day';
                        if (!isCurrentMonth) {
                            dayClass += ' calendar-day-other-month';
                        }
                        if (isToday) {
                            dayClass += ' calendar-day-today';
                        }
                        if (hasLeave) {
                            dayClass += ` calendar-day-leave calendar-day-${leaveStatus}`;
                        }
                        if (isCurrentMonth) {
                            dayClass += ' calendar-day-clickable';
                        }
                        
                        const isPastDate = date.isBefore(moment(), 'day');
                        if (isPastDate && !hasLeave) {
                            dayClass += ' calendar-day-disabled';
                        }
                        
                        return (
                            <div
                                key={index}
                                className={dayClass}
                                onClick={() => isCurrentMonth && !isPastDate && handleDateClick(date)}
                                title={
                                    isPastDate && !hasLeave 
                                        ? 'Cannot select past dates' 
                                        : hasLeave 
                                        ? `Leave: ${leaveStatus}` 
                                        : isCurrentMonth 
                                        ? 'Click to apply leave' 
                                        : ''
                                }
                            >
                                <span className="day-number">{date.format('D')}</span>
                                {hasLeave && (
                                    <span className={`leave-dot leave-dot-${leaveStatus}`}></span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const userOptions = users.map(u => ({
        value: u.id,
        label: u.name + (u.last_name ? ' ' + u.last_name : '')
    }));

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-4 px-4">
                <div className="row">
                    <div className="col-12">
                        <h3 className="mb-4">Apply Leave</h3>
                        
                        <div className="calendar-wrapper">
                            {renderCalendar()}
                        </div>

                        <div className="leave-legend">
                            <h5 className="mb-3">Legend:</h5>
                            <div className="d-flex flex-wrap gap-3">
                                <div className="legend-item">
                                    <span className="legend-dot leave-approved"></span>
                                    <span>Approved Leave</span>
                                </div>
                                <div className="legend-item">
                                    <span className="legend-dot leave-pending"></span>
                                    <span>Pending Leave</span>
                                </div>
                                <div className="legend-item">
                                    <span className="legend-dot leave-rejected"></span>
                                    <span>Rejected Leave</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Leave Application Modal */}
            {showModal && (
                <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className={`modal-header ${isEditMode ? (selectedLeave?.is_approved === 1 ? 'bg-success text-white' : selectedLeave?.is_approved === 2 ? 'bg-danger text-white' : 'bg-warning text-dark') : 'bg-primary text-white'}`}>
                                <h5 className="modal-title">
                                    {isEditMode ? 'Edit Leave' : 'Apply Leave'} - {selectedDate ? moment(selectedDate).format('DD MMMM YYYY') : ''}
                                    {isEditMode && selectedLeave && (
                                        <span className="badge bg-light text-dark ms-2">
                                            {selectedLeave.is_approved === 1 ? 'Approved' : selectedLeave.is_approved === 2 ? 'Rejected' : 'Pending'}
                                        </span>
                                    )}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={closeModal}
                                    style={isEditMode ? {} : {filter: 'invert(1)'}}
                                ></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    {isEditMode && selectedLeave && (
                                        <div className="alert alert-info">
                                            <strong>Leave Status:</strong> {
                                                selectedLeave.is_approved === 1 ? 'Approved' : 
                                                selectedLeave.is_approved === 2 ? 'Rejected' : 
                                                'Pending Approval'
                                            }
                                            {selectedLeave.report_to && selectedLeave.reportTo && (
                                                <div className="mt-2">
                                                    <strong>Report To:</strong> {selectedLeave.reportTo.name}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    <div className="form-group mb-3">
                                        <label>Leave Date</label>
                                        <input
                                            type="date"
                                            name="leave_applied"
                                            className="form-control"
                                            value={formData.leave_applied}
                                            onChange={handleChange}
                                            required
                                            disabled={isEditMode}
                                        />
                                    </div>

                                    <div className="form-group mb-3">
                                        <label>Reason</label>
                                        <textarea
                                            name="reason"
                                            className="form-control"
                                            rows="4"
                                            value={formData.reason}
                                            onChange={handleChange}
                                            placeholder="Enter reason for leave"
                                            required
                                        />
                                    </div>

                                    <div className="form-group mb-3">
                                        <label>Report To</label>
                                        <Select
                                            options={userOptions}
                                            onChange={handleReportToChange}
                                            value={userOptions.find(opt => opt.value === formData.report_to) || null}
                                            isSearchable={true}
                                            placeholder="Select person to report"
                                            isClearable={true}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    {isEditMode && (
                                        <button
                                            type="button"
                                            className="btn btn-danger me-auto"
                                            onClick={handleDelete}
                                            disabled={loading}
                                        >
                                            <i className="fa fa-trash me-1"></i>Delete
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeModal}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? (isEditMode ? 'Updating...' : 'Applying...') : (isEditMode ? 'Update Leave' : 'Apply Leave')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
